import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../apis';
import type { DefinitionRow } from '../../apis';

/** A definition waiting for someone else's review: a draft its author cannot publish themselves. */
export const awaitingReview = (rows: DefinitionRow[]): DefinitionRow[] => rows.filter((row) => row.status === 'draft' && !row.mine);

/** How many items a queue part holds: a list, or `{items}`. */
export function itemCount(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown[] }).items)) {
    return (value as { items: unknown[] }).items.length;
  }
  return 0;
}

/**
 * Something learned that only a person can back (#579): a claim whose earned tier is unconfirmed, or an agent's
 * observation. `agent_sourced` lessons are accepted as evidence and stay unconfirmed; a person's is confirmed.
 */
export interface Lesson {
  kind: 'claim' | 'observation'; id: string; who: string; actor: 'agent' | 'person'; agent_sourced: boolean;
  tier: string; text: string; quoted: boolean; metric: string | null; source: string | null; source_type: string;
  at: string | null; document_id: string | null; agent_id: string | null; can_review: boolean;
}
/** An Add to Knowledge request (#763 part B2), as the admins who decide it see it: its counts, never the document. */
export interface UploadRequest { upload_id: string; content_hash: string; name: string; pages: number | null; numbers: number; mismatches: number; by: string | null; at: string }
export type ReviewDefinition = Pick<DefinitionRow, 'id' | 'name' | 'display_name' | 'perspective' | 'status'> & { author_name?: string | null };
export interface ReviewQueueData {
  proposals: unknown[]; can_confirm: boolean; definitions: ReviewDefinition[]; lessons: Lesson[]; hidden_lessons: number;
  uploads: UploadRequest[];
  notes: Partial<Record<'proposals' | 'definitions' | 'lessons' | 'uploads', string>>; count: number;
}
export type LessonAction = 'confirm' | 'accept' | 'dismiss';
export interface LessonReview { id: string; kind: Lesson['kind']; lesson_id: string; action: LessonAction; metric: string | null; tier: string }

export async function readReviewQueue(): Promise<ReviewQueueData> {
  return (await apiClient.get<ReviewQueueData>('/api/knowledge/review-queue')).data;
}
export async function reviewLesson(lesson: Pick<Lesson, 'kind' | 'id'>, action: LessonAction): Promise<LessonReview> {
  return (await apiClient.post<LessonReview>('/api/knowledge/lessons/review', { kind: lesson.kind, id: lesson.id, action })).data;
}

export type NeedsReview = {
  proposals: number; definitions: ReviewDefinition[]; lessons: Lesson[]; hiddenLessons: number; uploads: UploadRequest[];
  /** null when no count could be read (#426): shown as no number, never as 0 */
  notes: ReviewQueueData['notes']; count: number | null; loaded: boolean; reload: () => void;
};

/**
 * Knowledge › Needs review (#579): proposed relationships, lessons and definitions awaiting review, composed by the
 * server — its `count` is the rail's Knowledge badge.
 */
export function useNeedsReview(): NeedsReview {
  const [state, setState] = useState<Omit<NeedsReview, 'reload'>>({ proposals: 0, definitions: [], lessons: [], hiddenLessons: 0, uploads: [], notes: {}, count: null, loaded: false });
  const request = useRef(0);
  const reload = useCallback(() => {
    const id = ++request.current;
    readReviewQueue().then((data) => {
      if (id !== request.current) return;
      // a reply without a count is not a count of zero (#426): its rows still show, its count shows as none
      setState({ proposals: itemCount(data.proposals), definitions: data.definitions || [], lessons: data.lessons || [],
        hiddenLessons: typeof data.hidden_lessons === 'number' ? data.hidden_lessons : 0, uploads: data.uploads || [], notes: data.notes || {},
        count: typeof data.count === 'number' ? data.count : null, loaded: true });
    }, () => {
      if (id !== request.current) return;
      // the last count stands (none before the first read), and every part says it could not be read
      const note = 'Temporarily unavailable. Try again shortly.';
      setState((s) => ({ ...s, notes: { proposals: note, definitions: note, lessons: note, uploads: note } }));
    });
  }, []);
  useEffect(() => {
    reload();
    return () => { request.current += 1; };
  }, [reload]);
  return { ...state, reload };
}
